import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap'
import {
  Content
} from 'components/Content'
import {
  CardModalView,
  ViewBody,
  HeaderButton,
} from 'components/Sidebar'
import CardComponent from 'components/Card'
import KanBoIcon from 'components/KanBoIcon'
import Scrollbars from 'components/Scrollbars'
import classes from './DateConflicts.module.scss'
import moment from 'moment'
import { useCardContext } from 'dataProviders/Card'
import SmartCard from 'components/SmartCard'
import { useSelector } from 'react-redux'

const CardName = ({ id, isBusy, isOtherCard }) => {
  const name = useSelector(state => state['view-board'].cards[id]?.name)

  return (
    <CardComponent
      linkName
      id={id}
      name={name}
      isBusy={isBusy}
      className={`${classes.card}${isOtherCard ? ` ${classes.otherCard}` : ''}`}
    />
  )
}

const Card = ({
  id,
  isOtherCard,
}) =>
  <SmartCard
    id={id}
    component={CardName}
    isOtherCard={isOtherCard}
    hover
  />

const DateType = ({ type }) =>
  <span className={classes.dateType}>
    <FormattedMessage
      id={(() => {
        switch (type) {
          case 'startDate':
            return 'CARD_START_DATE'
          case 'dueDate':
            return 'CARD_DUE_DATE'
          default:
            return null
        }
      })()}
    />
  </span>

const Date = ({ reverse, type, date }) =>
  <div
    className={`${classes.date}${reverse ? ` ${classes.reverse}` : ''}`}
  >
    <span
      className={classes.dateTile}
    >
      <KanBoIcon
        name={(() => {
          switch (type) {
            case 'startDate':
              return 'kanbo-16-start-date'
            case 'dueDate':
              return 'kanbo-16-due-date'
            default:
              return null
          }
        })()}
        className={classes.icon}
      />
      {moment(date).format('D MMM')}
    </span>
    <DateType type={type} />
  </div>

const RelationType = ({ children, iconName, className }) =>
  <span className={`${classes.relationType}${className ? ` ${className}` : ''}`}>
    <KanBoIcon
      name={iconName}
      className={classes.relationTypeIcon}
    />
    {children}
  </span>

export default function DateConflicts ({
  errorCards,
  title,
}) {
  const intl = useIntl()

  const cardContext = useCardContext()

  return (
    <CardModalView
      title={title}
      inlineMenu={
        <HeaderButton
          icon={<KanBoIcon name='kanbo-16-info' />}
          onClick={() => {
            window.open('https://community.kanboapp.com/knowledge-bases/11/articles/5016-child-card-date-dependencies', '_blank')
          }}
        />
      }
    >
      <Scrollbars
        component={ViewBody}
      >
        {errorCards.length === 0 ? (
          <Content style={{ padding: '15px 20px', textAlign: 'center' }}>
            <FormattedMessage id='CARD_DATE_CONFLICT_NO_MORE' />
          </Content>
        ) : (
          errorCards.map(relationError => {
            if (relationError.relationType === 'sequence') {
              return (
                <div
                  key={`${relationError.prevId}-${relationError.nextId}`}
                  className={`${classes.relationError} ${classes.sequenceRelationError}`}
                >
                  <div className={classes.sequenceCards}>
                    <div className={classes.prevCard}>
                      <RelationType
                        iconName={'kanbo-16-relation-predecessor'}
                      >
                        <FormattedMessage id='CARD_RELATION_PREDECESSOR' />
                      </RelationType>
                      <Card
                        id={relationError.prevId}
                        isOtherCard={relationError.prevId !== cardContext.id}
                      />
                    </div>
                    <div className={classes.sequenceDivider} />
                    <div className={classes.nextCard}>
                      <RelationType
                        iconName={'kanbo-16-relation-successor'}
                        className={classes.relationTypeNext}
                      >
                        <FormattedMessage id='CARD_RELATION_SUCCESSOR' />
                      </RelationType>
                      <Card
                        id={relationError.nextId}
                        isOtherCard={relationError.nextId !== cardContext.id}
                      />
                    </div>
                  </div>
                  {relationError.errors.map(error =>
                    <OverlayTrigger
                      key={`${error.prevDateType}-${error.nextDateType}`}
                      placement='top'
                      overlay={
                        <Tooltip>
                          {intl.formatMessage({ id: error.formattedMessageId })}
                        </Tooltip>
                      }
                    >
                      <div
                        className={classes.sequenceError}
                      >
                        <Date
                          reverse
                          type={error.prevDateType}
                          date={error.prevDate}
                        />
                        <div className={classes.dateDiff}>
                          {(() => {
                            const daysDiff = Math.abs(moment(error.nextDate).diff(error.prevDate, 'days'))

                            if (daysDiff < 1) {
                              return (
                                <FormattedMessage
                                  id='CARD_DATE_CONFLICT_DIFF_HOURS'
                                  values={{
                                    hours: Math.abs(moment(error.nextDate).diff(error.prevDate, 'hours'))
                                  }}
                                />
                              )
                            } else {
                              return (
                                <FormattedMessage
                                  id='CARD_DATE_CONFLICT_DIFF_DAYS'
                                  values={{
                                    days: daysDiff
                                  }}
                                />
                              )
                            }
                          })()}
                        </div>
                        <Date
                          type={error.nextDateType}
                          date={error.nextDate}
                        />
                      </div>
                    </OverlayTrigger>
                  )}
                </div>
              )
            } else if (relationError.relationType === 'child') {
              return (
                <div
                  key={`${relationError.parentId}-${relationError.childId}`}
                  className={`${classes.relationError} ${classes.childRelationError}`}
                >
                  <div className={classes.childRelationErrorCards}>
                    <div>
                      <RelationType
                        iconName={'kanbo-16-relation-parent'}
                      >
                        <FormattedMessage id='CARD_RELATION_PARENT' />
                      </RelationType>
                    </div>
                    <Card
                      id={relationError.parentId}
                      isOtherCard={relationError.parentId !== cardContext.id}
                    />
                    <div className={classes.childDivider}>
                      <RelationType
                        iconName={'kanbo-16-relation-child'}
                      >
                        <FormattedMessage id='CARD_RELATION_CHILD' />
                      </RelationType>
                    </div>
                    <Card
                      id={relationError.childId}
                      isOtherCard={relationError.childId !== cardContext.id}
                    />
                  </div>
                  {relationError.errors.map(error =>
                    <OverlayTrigger
                      key={`${error.parentDateType}-${error.childDateType}`}
                      placement='top'
                      overlay={
                        <Tooltip>
                          {intl.formatMessage({ id: error.formattedMessageId })}
                        </Tooltip>
                      }
                    >
                      <div
                        className={classes.childError}
                      >
                        <Date
                          reverse
                          type={error.parentDateType}
                          date={error.parentDate}
                        />
                        <div className={classes.childDivider}>
                          <div className={classes.dateDiff}>
                            {(() => {
                              const daysDiff = Math.abs(moment(error.childDate).diff(error.parentDate, 'days'))

                              if (daysDiff < 1) {
                                return (
                                  <FormattedMessage
                                    id='CARD_DATE_CONFLICT_DIFF_HOURS'
                                    values={{
                                      hours: Math.abs(moment(error.childDate).diff(error.parentDate, 'hours'))
                                    }}
                                  />
                                )
                              } else {
                                return (
                                  <FormattedMessage
                                    id='CARD_DATE_CONFLICT_DIFF_DAYS'
                                    values={{
                                      days: daysDiff
                                    }}
                                  />
                                )
                              }
                            })()}
                          </div>
                        </div>
                        <Date
                          type={error.childDateType}
                          date={error.childDate}
                        />
                      </div>
                    </OverlayTrigger>
                  )}
                </div>
              )
            }

            return null
          })
        )}
      </Scrollbars>
    </CardModalView>
  )
}
